import { Box, Button, Columns, Icon, Level } from 'react-bulma-components';
import React, { useEffect, useRef, useState } from 'react';
import {
  faDrum,
  faICursor,
  faMusic,
  faPause,
  faPen,
  faPlay,
  faRedo,
  faSlidersH,
  faStepBackward,
  faTachometerAlt,
  faVolumeUp
} from '@fortawesome/free-solid-svg-icons';

import { Controller } from './Controller';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconDoubleBass from '../icons/IconDoubleBass';
import IconPiano from '../icons/IconPiano';
import { Interval } from "@tonaljs/tonal";
import MIDIEditor from '../MIDIInspector/MIDIEditor';
import Switch from '../switch';
import { useMusicEngine } from '../../audio/useMusicEngine';

const Player = ({
  playerId: externalPlayerId,
  src,
  midiUrl,
  tonic,
  midiEditor,
  onSaved,
  reloadToken,
  chooseFile,
  transpose,
  editorOpen,
  setEditorOpen,
  bpm,
  showCursorToggle,
  ...props
}) => {
  midiUrl = midiUrl ?? src; // Tillåt src som alias för midiUrl
  // Om parent skickar in ett ID → använd det
  // Annars generera ett stabilt ID en gång
  const internalIdRef = useRef(
    externalPlayerId ?? `player-${Math.random().toString(36).slice(2, 10)}`
  );

  const playerId = internalIdRef.current;

  const {
    statusMsg,
    activePlayerId,
    players,
    mixer,
    ensureSynth,
    loadMidiFromArrayBuffer,
    loadMidiFromUrl,
    play,
    saveParsedMidi,
    startFromBeginning,
    pause,
    seekByProgress,
    setMainVolume,
    setMainPan,
    setChannelVolume,
    getParsedMidi,
    updateParsedMidi,
    setTranspose,
    setPlaybackRate,
    setBpmOverride,
    setLoopEnabled,
    setCountInEnabled,
    setCountInVolume,
    setCursorEnabled,
  } = useMusicEngine();

  // Bara en underpanel synlig åt gången - 'mixer' | 'speed' | 'countIn' | null
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = name => {
    setOpenPanel(prev => (prev === name ? null : name));
  };
  const showMixer = openPanel === 'mixer';
  const showSpeed = openPanel === 'speed';
  const showCountIn = openPanel === 'countIn';
  const showEditor = editorOpen;
  const setShowEditor = setEditorOpen;


  const id = playerId;

  const playerState =
    players[id] || {
      progress: 0,
      currentTime: 0,
      duration: 0,
      timeLabel: '--:--/--:--',
      hasSeq: false,
      transpose: 0,
      playbackRate: 1,
      bpmOverride: null,
      loopEnabled: false,
      countInEnabled: true,
      countingIn: false,
      cursorEnabled: true,
    };

  const isActive = activePlayerId === id;

  // Stoppa uppspelningen om den här spelaren försvinner ur vyn (byte av sida,
  // stängd editor, etc.) medan den spelar - annars fortsätter ljudet i
  // bakgrunden utan att någon synlig kontroll längre visar det. pause() är en
  // no-op om den här spelaren inte är den aktiva, så det är säkert att alltid
  // anropa den vid unmount. En ref används så att cleanupen alltid använder
  // den senaste pause-funktionen utan att effekten behöver köras om varje
  // render (pause byts ut på varje render av providern).
  const pauseRef = useRef(pause);
  useEffect(() => {
    pauseRef.current = pause;
  }, [pause]);
  useEffect(() => {
    return () => {
      pauseRef.current(id);
    };
  }, [id]);

  // Ladda MIDI
  useEffect(() => {
    if (!midiUrl) return;
    console.log('Player useEffect loadMidiFromUrl', id, midiUrl);
    if (isActive) {
        pause(id);
  }
    ensureSynth().then(() => {
      loadMidiFromUrl(id, midiUrl).catch(err => {
       // console.error('Failed to load MIDI from URL', err);
      });
    });
  }, [id, midiUrl, reloadToken]);

  // Applicera transpose när prop ändras (och/eller efter load)
  useEffect(() => {
    let semiTonesDiff = 0;
    if (tonic) {
      // Justera transpose baserat på skillnaden mellan spelarens tonic argument och selected keytonic
      console.log("Key: "+props?.keytonic+", Tonic is set to " + tonic + " with transpose " + transpose);
      const keyDiff =Interval.semitones(Interval.distance(tonic, props?.keytonic));
      const wrapped = ((keyDiff + 6) % 12) - 6; // Shortest distance between keys, e.g. -2 instead of +10
      semiTonesDiff += wrapped;
    }
    if (typeof transpose === 'number') {
      console.log("Applying transpose " + transpose + " with semitones diff " + semiTonesDiff+" = "+(transpose+semiTonesDiff));
      setTranspose(id, transpose+semiTonesDiff);
    }
  }, [id, transpose, tonic, props?.keytonic]);

  // Om bpm-prop är satt används den istället för tempot i midifilen. Sätts
  // den till undefined/null igen faller spelaren tillbaka på filens eget tempo.
  useEffect(() => {
    console.log("BPM type is " + typeof bpm);
    setBpmOverride(id, bpm);
  }, [id, bpm]);

  const onLocalFileChange = async e => {
    if (!e.target.files || !e.target.files[0]) return;
    const buf = await e.target.files[0].arrayBuffer();
    await loadMidiFromArrayBuffer(id, buf);
    setTranspose(id, transpose);
  };

  const handleSave = () => {
    saveParsedMidi(id)
    .then(() => onSaved?.())
    .catch(err => console.error("Save failed", err));
  }

  const handleStart = () => {
    if (!playerState.hasSeq) return;
    startFromBeginning(id);
  };

  const handlePlayPause = () => {
    if (!playerState.hasSeq) return;

    if (isActive) {
      pause(id);
    } else {
      play(id);
    }
  };

  const handleSeek = val => {
    if (!playerState.hasSeq) return;
    const numVal = Number(val);
    const ratio = numVal / 1000;
    seekByProgress(id, ratio);
  };

  const adjustMainVolume = val => {
    setMainVolume(val);
  };

  const adjustMainPan = val => {
    setMainPan(val);
  };

  const adjustChannelVolume = (channel, val) => {
    setChannelVolume(channel, val);
  };

  const adjustPlaybackRate = val => {
    setPlaybackRate(id, val);
  };

  const toggleLoop = () => {
    setLoopEnabled(id, !playerState.loopEnabled);
  };

  const toggleCursor = () => {
    setCursorEnabled(id, playerState.cursorEnabled === false);
  };

  const toggleCountIn = checked => {
    setCountInEnabled(id, checked);
    if (!checked) {
      setOpenPanel(null);
    }
  };

  const adjustCountInVolume = val => {
    setCountInVolume(val);
  };

  const parsedMIDI = getParsedMidi(id);

  const handleMidiEdited = midi => {
    updateParsedMidi(id, midi);
  };

  return (
<Box backgroundColor='white' style={{ padding: "0.8rem" }}>
          {chooseFile &&
          <div>
          <input
            onChange={onLocalFileChange}
            accept=".mid, .rmi"
            id="midi_input"
            type="file"
          />
        </div>}
        {false &&
          <div><i>Debug - BPM: {bpm}, Transpose: {transpose}, Key: {props?.keytonic}, Tonic: {tonic}</i></div>
        }
          <Level>
            <Level.Side align="left">
              <Level.Item>
                <Button.Group hasAddons size="small" >
                  <Button
                    onClick={handleStart}
                    disabled={!playerState.hasSeq}
                    renderAs="span"
                  >
                    <Icon>
                      <FontAwesomeIcon icon={faStepBackward} />
                    </Icon>
                  </Button>

                  <Button
                    onClick={handlePlayPause}
                    renderAs="span"
                    disabled={!playerState.hasSeq}
                  >
                    <Icon>
                      <FontAwesomeIcon
                        icon={isActive ? faPause : faPlay}
                      />
                    </Icon>
                  </Button>
                </Button.Group>
              </Level.Item>

              <Level.Item>
                <div className="is-family-monospace is-size-7">
                  {playerState.countingIn ? 'Counting in…' : playerState.timeLabel}
                </div>
              </Level.Item>
            </Level.Side>

            <Level.Item>
              <Controller
                min={0}
                max={1000}
                currentValue={playerState.progress * 1000}
                onChange={e => handleSeek(e.target.value)}
              />
            </Level.Item>

            <Level.Side align="right">
              <Level.Item>
                <Button.Group hasAddons size="small" >
                <Button
                  size="small"
                  onClick={() => togglePanel('mixer')}
                  renderAs="span"
                >
                  <Icon>
                    <FontAwesomeIcon icon={faSlidersH} />
                  </Icon>
                </Button>
                <Button
                  size="small"
                  onClick={() => togglePanel('speed')}
                  renderAs="span"
                >
                  <Icon>
                    <FontAwesomeIcon icon={faTachometerAlt} />
                  </Icon>
                </Button>
                <Button
                  size="small"
                  color={playerState.countInEnabled !== false ? 'info' : undefined}
                  onClick={() => togglePanel('countIn')}
                  renderAs="span"
                >
                  <Icon>
                    <strong>4/4</strong>
                  </Icon>
                </Button>
                {showCursorToggle &&
                <Button
                  size="small"
                  color={playerState.cursorEnabled !== false ? 'info' : undefined}
                  onClick={toggleCursor}
                  renderAs="span"
                >
                  <Icon>
                    <FontAwesomeIcon icon={faICursor} />
                  </Icon>
                </Button>
                }
                <Button
                  size="small"
                  color={playerState.loopEnabled ? 'info' : undefined}
                  onClick={toggleLoop}
                  renderAs="span"
                >
                  <Icon>
                    <FontAwesomeIcon icon={faRedo} />
                  </Icon>
                </Button>
              {midiEditor && parsedMIDI &&
                <Button
                  size="small"
                  onClick={() => setShowEditor(prev => !prev)}
                  renderAs="span"
                >
                  <Icon>
                    <FontAwesomeIcon icon={faPen} />
                  </Icon>
                </Button>
                }
                </Button.Group>
              </Level.Item>
            </Level.Side>
          </Level>

          {showMixer && (
            <>
              <Level style={{ marginBottom: 16 }}>
                <Level.Item>
                  <Icon>
                    <FontAwesomeIcon icon={faVolumeUp} />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={mixer.mainVolume}
                    onChange={e =>
                      adjustMainVolume(+e.target.value)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <IconPiano />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={mixer.pianoVolume}
                    onChange={e =>
                      adjustChannelVolume(0, +e.target.value)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <IconDoubleBass />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={mixer.bassVolume}
                    onChange={e =>
                      adjustChannelVolume(1, +e.target.value)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <FontAwesomeIcon icon={faDrum} />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={mixer.drumsVolume}
                    onChange={e =>
                      adjustChannelVolume(9, +e.target.value)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>

              <Level>
                <Level.Item>
                  <Icon>
                    <FontAwesomeIcon icon={faMusic} />
                  </Icon>
                  <Controller
                    min={0}
                    max={100}
                    currentValue={mixer.melodyVolume}
                    onChange={e =>
                      adjustChannelVolume(2, +e.target.value)
                    }
                    showOutput={false}
                  />
                </Level.Item>
              </Level>
            </>
          )}

          {showSpeed && (
            <Level style={{ marginBottom: 16 }}>
              <Level.Item>
                <Icon>
                  <FontAwesomeIcon icon={faTachometerAlt} />
                </Icon>
                <Controller
                  min={0.5}
                  max={1.5}
                  step={0.1}
                  currentValue={playerState.playbackRate ?? 1}
                  onChange={e => adjustPlaybackRate(+e.target.value)}
                  showOutput
                />
              </Level.Item>
            </Level>
          )}

          {showCountIn && (
            <Level style={{ marginBottom: 16 }}>
              <Level.Side align="left">
                <Level.Item style={{ marginRight: 16 }}>
                  <Icon style={{ marginRight: 16 }}>
                    <strong>4/4</strong>
                 </Icon>
                  <Switch
                    name={`countin-${id}`}
                    label="Count in"
                    checked={playerState.countInEnabled !== false}
                    onChange={e => toggleCountIn(e.target.checked)}
                  />
                </Level.Item>
              </Level.Side>
              <Level.Item>
                <Icon>
                  <FontAwesomeIcon icon={faVolumeUp} />
                </Icon>
                <Controller
                  min={0}
                  max={100}
                  currentValue={mixer.countInVolume}
                  onChange={e => adjustCountInVolume(+e.target.value)}
                  showOutput={false}
                />
              </Level.Item>
            </Level>
          )}

          {showEditor && (
          <MIDIEditor
            midiUrl={midiUrl+reloadToken}
            parsedMIDI={parsedMIDI}
            onParsedMIDIChange={handleMidiEdited}
            onSave={handleSave}
          />
        )}
        </Box>

  );
};

export default Player;
